import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import * as AWS from 'aws-sdk';
import * as STS from 'aws-sdk/clients/sts';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { CognitoIdentityCredentials } from 'aws-sdk/global';
import { CognitoAuth } from 'amazon-cognito-auth-js-promises';
import { CognitoUserPool, AuthenticationDetails, CognitoUser } from 'amazon-cognito-identity-js';

/*
  Authenticates users and restricts view based on role.

  The 'authenticate' function should be invoked in each component / route on initialization.

  Steps:
  1. Check localStorage for an idToken
  2. If an idToken exists, use it to generate temporary AWS credentials
    a. If it's expired, refresh it using a refresh token
    b. If it's expired and there's no refresh token, generate new tokens using an Authorization Code
  3. If no idToken exists in localStorage, or if the existing idToken and refreshToken both expired, then generate new tokens using OAuth2.0
    a. /authorize redirect
    b. /tokens POST
    c. Use tokens to generate temporary AWS credentials
    d. Store tokens in localStorage
*/


@Injectable({
  providedIn: 'root'
})
export class AuthService {

  idtKey: string = 'bui_idt';
  expKey: string = 'bui_exp';
  rtKey: string = 'bui_rt';
  session: any;

  webDomain = environment.webDomain;
  scope = '';
  authCode: string;
  authEndpoint =`https://${environment.cognitoDomain}.auth.${environment.region}.amazoncognito.com/oauth2/authorize`
  onLoginCallbackUrl = `https://${window.location.hostname}/auth`;
  cognitoHost = `https://${environment.cognitoDomain}.auth.${environment.region}.amazoncognito.com`
  tokenEndpoint = `${this.cognitoHost}/oauth2/token`;
  memberOf = [];
  params = [];
  userPool: any;
  username: string;
  currentUser: string = '';
  token: string;
  cognitoAuth: CognitoAuth;
  authenticated = false;
  private authstarted = false;
  private authorizing = true;
  refreshInterval: any;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    environment.authScopes.forEach(s=> {
      this.scope += `${s} `;
    });
    AWS.config.region = environment.region;
    let poolData = {
      UserPoolId: environment.cognitoUserPoolId,
      ClientId: environment.cognitoClientId
    }
    this.userPool = new CognitoUserPool(poolData);
    this.initAuth();
  }

  async initAuth() {
    if (window.location.pathname === '/logout') {
      this.authorizing = false;
      return null
    }
    if (this.authstarted) {
      return false;
    }
    this.authstarted = true;
    this.authorizing = true;
    let authData = {
      ClientId : environment.cognitoClientId,
      AppWebDomain : `${environment.cognitoDomain}.auth.us-east-1.amazoncognito.com`,
      TokenScopesArray : [
        'phone',
        'email',
        'profile',
        'openid'
      ],
      RedirectUriSignIn : `${window.location.origin}`,
      RedirectUriSignOut : `${window.location.origin}/logout`,
      UserPoolId : environment.cognitoUserPoolId
    };

    this.cognitoAuth = new CognitoAuth(authData);

    this.cognitoAuth.useCodeGrantFlow();

    const curUrl = window.location.href;
    await this.cognitoAuth.parseCognitoWebResponse(curUrl);
    const session = await this.cognitoAuth.getSession();
    localStorage.setItem(this.idtKey,session.getIdToken().getJwtToken());
    this.setCredentials(session.getIdToken().getJwtToken());
    this.setRefreshInterval();
    new STS().getCallerIdentity((err: any) => {
      this.authorizing = false;
      if (err) {
        console.error('STS Error: ', err);
        return err;
      }
      return true
    });
  };

  async setRefreshInterval() {
    clearInterval(this.refreshInterval);
    this.refreshInterval = setInterval(() => {
      let user = this.userPool.getCurrentUser();
      if (user) {
        user.getSession(async (err: any, session: any) => {
          if (err) {
            console.error('Error getting session', err)
            return false;
          }
          let refreshToken = session.getRefreshToken();
          if (refreshToken) {
            console.log('Refreshing', refreshToken);
            this.refreshSession(refreshToken, session.getIdToken().getJwtToken());
          }
        })
      }
    }, 1800000)
  }


  // check for an existing session, returning true or false on the callback accordingly
  async isAuthenticated() {
    if (this.authorizing) {
      await new Promise((resolve) => {
        let authIntCount = 0;
        const authInt = setInterval(() => {
          if (!this.authorizing) {
            clearInterval(authInt);
            this.authenticated = true;
            resolve(true)
          }
          if (++authIntCount > 20) {
            console.error('Authorization took too long');
            clearInterval(authInt);
            this.authenticated = false;
            resolve(false)
          }
        }, 500);
      })
    }
    let user = this.userPool.getCurrentUser();
    if (user) {
      return new Promise((resolve) => {
        user.getSession(async (err: any, session: any) => {
          this.session = session;
          localStorage.setItem(this.idtKey, session.idToken.jwtToken);
          if (err) {
            console.error('Get Session Error: ', err);
            this.authenticated = false;
            resolve(this.cognitoAuth.getSession()); //Refresh Cognito session, user will have to log in again
          }
          if (session) {
            if (session && session.isValid()) {
              const memberof = localStorage.getItem('memberOf');
              if (memberof && (memberof[0] !== 'false')) {
                this.memberOf = localStorage.getItem('memberOf').split(',');
              }
              this.username = session.accessToken.payload.username;
              this.currentUser = this.generateCurrentUserString(this.username);
              this.token = session.idToken.jwtToken;
              const setCredentials = await this.setCredentials(session.getIdToken().getJwtToken());
              this.authenticated = true;
              resolve(setCredentials)
            } else {
              console.log('Session expired...attempting to use refresh token..');
              this.authenticated = false;
              this.cognitoAuth.getSession(); //Refresh Cognito session, user will have to log in again
              let refreshToken = session.getRefreshToken();
              if (refreshToken) {
                resolve(this.refreshSession(refreshToken, session.getIdToken().getJwtToken()));
              }
            }
          }
        });
      })
    } else {
      return false;
    }
  }

  //the value to be shown for 'updatedBy' fields
  generateCurrentUserString(username: string){
    return username.split('_')[1].split('@')[0];
  }

  // given a JWT, set the credentials object
  async setCredentials(token: string) {
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: environment.identityPoolId,
      Logins: {
        [`cognito-idp.${environment.region}.amazonaws.com/${environment.cognitoUserPoolId}`]: token
      }
    });
    return await this.refreshCredentials();
  }

  // use a refresh token to generate a new id token
  refreshSession(refreshToken: any, idToken: any) {
    return new Promise((resolve, reject) => {
      let user = this.userPool.getCurrentUser();
      if (user && refreshToken && idToken) {
        user.refreshSession(refreshToken, (err: any, session: any) => {
          if (err) {
            console.error('Refresh Session Error: ', err);
            reject(err);
          }
          if (session && session.isValid()) {
            resolve(this.setCredentials(session.getIdToken().getJwtToken()));
          }
        });
      }
    })
  }

  // refresh the credentials object
  refreshCredentials() {
    return new Promise ((resolve, reject) => {
      if (AWS.config.credentials) {
        return (AWS.config.credentials as CognitoIdentityCredentials).refresh((error) => {
          if (error) {
            console.error(error);
            reject(error)
          } else {

            resolve(true);
          }
        });
      }
    })
  }

  setNewPassAndLogin(username: string, currentPass: string, newPass: string) {
    return this.login(username, currentPass, newPass);
  }

  // password-based authentication
  login(username: string, password: string, newPass?: any) {
    // Old login process
    // const authUrl = `${this.authEndpoint}?response_type=code&client_id=${environment.cognitoClientId}&redirect_uri=${this.onLoginCallbackUrl}&idp_identifier=${environment.identityProvider}&scope=${this.scope.trim()}`
    // this.sendToUrl(authUrl);
    //
    let authenticationData = {
      Username: username,
      Password: password,
    };
    let userData = {
      Username: username,
      Pool: this.userPool
    };
    let authDetails = new AuthenticationDetails(authenticationData);
    let user = new CognitoUser(userData);
    let challengeCallbacks = {
      onSuccess: (result: any) => {
          return 'SUCCESS';
      },
      onFailure: (err: any) => {
          console.error('Challenge Error: ', err);
          return err;
      }
    }
    user.authenticateUser(authDetails, {
      newPasswordRequired: () => {
        if (newPass) {
          user.completeNewPasswordChallenge(newPass, null, challengeCallbacks);
        } else {
          return 'NEW_PASS';
        }
      },
      onSuccess: (result: any) => {
        this.setCredentials(result.getIdToken().getJwtToken());
        new STS().getCallerIdentity((err: any) => {
          if (err) {
            console.error('STS Error: ', err);
            this.authenticated = false;
          } else {
            this.authenticated = true;
          }
        });
        return 'SUCCESS';
      },
      onFailure: (err: any) => {
        console.error('AuthFailure: ', err);
        return err;
      }
    });
  }

  getToken() {
    return this.token;
  }

  // clears localstorage of tokens, effectively signing the user out
  logout() {
    this.authstarted = false;
    this.authorizing = false;
    this.authenticated = false;
    let user = this.userPool.getCurrentUser();
    localStorage.clear();
    this.memberOf = [];
    if (user) {
      this.cognitoAuth.signOut()
    }
  }

  redirectToLogin() {
    this.cognitoAuth.getSession();
  }

  //
  // Previous login functions
  //
  getAuthorization = async () => {

    await new Promise((resolve, reject) => {
      let iter = 0;

      if (!this.authstarted) {
        this.initAuth();
        resolve(false);
      }

      if (this.authorizing) {
        const authInterval = setInterval(() => {

          if (!this.authorizing) {
            clearInterval(authInterval);
            resolve(true);
          }
          if (iter > 15) {
            clearInterval(authInterval);
            reject(false)
          }
          iter++
        }, 500)
      } else {
        resolve(true);
      }

    })
    let idt = localStorage.getItem(this.idtKey);
    if (!idt) {
      return false;
    } else {
      await this.getAndSetCredentials();
      const lambda = new AWS.Lambda({ region: environment.region });
      const params = {
        FunctionName: environment.authzLambdaName,
        Payload: JSON.stringify({
          idToken: idt
        })
      }
      let response = await lambda.invoke(params).promise();
      if (environment.production) {
        return response
      } else {
        return {
          Payload: JSON.stringify({
            authz_result: ['DNISAdmin', 'WBConfig', 'WBConfigNPD', 'DNISAdminNPD', 'bui'],
            authn_result: 'success'
          }),
          StatusCode: 200
        };
      }
    }
  }

  // authn
  authenticate = async () => {
    let idToken = localStorage.getItem(this.idtKey);
    let expiresOn = localStorage.getItem(this.expKey);
    let refreshToken = localStorage.getItem(this.rtKey);

    if (idToken) {
      return this.getAndSetCredentials();
    }

    if (Number(expiresOn) <= (Date.now() / 1000)) {
      return this.refreshTokens(refreshToken);
    }
  }

  storeTokenData = (tokenResponse) => {
    let idt = tokenResponse.id_token;
    let exp = tokenResponse.expires_in;
    let rt = tokenResponse.refresh_token;
    if (idt) localStorage.setItem(this.idtKey, idt);
    if (exp) localStorage.setItem(this.expKey, exp + (Date.now() / 1000));
    if (rt) localStorage.setItem(this.rtKey, rt);
  }

  performTokenRequest = (params) => {
    return this.http.post(this.tokenEndpoint, params).toPromise();
  };

  refreshTokens = async (refreshToken: string) => {
    let queryParams = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('refresh_token', `${refreshToken}`)
      .set('client_id', `${environment.cognitoClientId}`);
    try {
      let response: any = await this.performTokenRequest(queryParams);
      if (!response) {
        return false;
      }

      if (response.id_token) {
        let success = await this.getAndSetCredentials(response.id_token);
        if (success) {
          this.storeTokenData(response);
          return true;
        } else {
          return false;
        }
      }
      return true;
    } catch (e) {
      console.error('Could not refresh tokens: ', e);
      return false;
    }
  };

  getNewTokens = async (authCode: any) => {
    let queryParams = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('code', `${authCode}`)
      .set('client_id', `${environment.cognitoClientId}`)
      .set('redirect_uri', `${this.onLoginCallbackUrl}`);
    try {
      let response: any = await this.performTokenRequest(queryParams);
      if (!response) {
        return false;
      }
      if (response.id_token) {
        let success = await this.getAndSetCredentials(response.id_token);
        if (success) {
          this.storeTokenData(response);
          return true;
        } else {
          return false;
        }
      }
      return true;
    } catch (e) {
      console.error('Could not refresh tokens: ', e);
      return false;
    }
  };

  getAndSetCredentials = async (idt: string = null) => {
    if (idt === null) idt = localStorage.getItem(this.idtKey);
    try {
      idt = this.session.idToken.jwtToken;
    } catch(err) {
      return false
    }
    if (!idt) {
      return false;
    }
    AWS.config.region = environment.region;
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: environment.identityPoolId,
      Logins: {
        [`cognito-idp.${environment.region}.amazonaws.com/${environment.cognitoUserPoolId}`]: idt
      }
    });
    return new Promise((resolve, reject) => {
      (AWS.config.credentials as any).refresh((err: any) => {
        if (err) {
          console.error('Could not refresh credentials: ', err);
          this.logout();
          this.router.navigate(['/auth']);
          reject(false);
        } else {
          resolve(true);
        }
      });
    });
  }

  sendToUrl(url: string) {
    window.location.href = url;
  }

  hasPermission(role: string) {
    if(this.memberOf.includes(role) || this.memberOf.includes(environment.roles.telecom)){
      return true;
    }
    return false;
  }
}
